/**
 * Custom design request API module — SH-CDR-001, extended SH-DSVC-101.
 *
 * Typed helpers for the public submit endpoint and the admin list/detail/
 * status-update/assign/create-quote endpoints. Callers (containers, TanStack
 * Query hooks, thunks) MUST import from this module — never call the shared
 * axios client directly.
 *
 * Backend: PL-CDR-001 (public submit), PL-CDR-002 (admin list/detail/update),
 * PL-DSVC-101 (assign, reference files, extended status set), PL-DSVC-102
 * (create-quote-from-request).
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  AdminCustomDesignRequestListResponseData,
  CreateQuoteFromDesignRequestResponseData,
  CustomDesignRequestDTO,
  CustomDesignRequestResponseData,
  CustomDesignRequestsPaginationMeta,
  DesignRequestAssigneeDTO,
  DesignRequestAssigneesResponseData,
  FetchCustomDesignRequestsParams,
  SubmitCustomDesignRequestPayload,
  UpdateCustomDesignRequestStatusPayload,
} from '@dfq/shared/types/customDesignRequest';

// ---------------------------------------------------------------------------
// Result shapes returned to callers
// ---------------------------------------------------------------------------

export interface FetchCustomDesignRequestsResult {
  customDesignRequests: CustomDesignRequestDTO[];
  pagination: CustomDesignRequestsPaginationMeta;
}

// ---------------------------------------------------------------------------
// Public submit
// ---------------------------------------------------------------------------

/**
 * Submit a custom design request.
 *
 * Route: `POST /api/v1/custom-design-requests`
 * Auth:  public — no OAuth client credentials required. `security: []`.
 *
 * The platform stamps `user_id` from the authenticated storefront session
 * (when present) and ignores any client-supplied value, so `userId` is
 * intentionally not part of the payload type.
 *
 * `artwork_file_ids` / `product_type` are omitted from the body entirely
 * when absent — an empty array is meaningfully different from "no key"
 * to the Form Request's `nullable` rule, and omitting keeps the wire body
 * minimal for the common no-attachment / no-product-type case.
 */
export async function submitCustomDesignRequest(
  payload: SubmitCustomDesignRequestPayload,
): Promise<CustomDesignRequestDTO> {
  try {
    const body: Record<string, unknown> = {
      name: payload.name,
      email: payload.email,
      description: payload.description,
      phone: payload.phone ?? null,
      company: payload.company ?? null,
    };
    if (payload.product_type) {
      body.product_type = payload.product_type;
    }
    if (payload.artwork_file_ids && payload.artwork_file_ids.length > 0) {
      body.artwork_file_ids = payload.artwork_file_ids;
    }

    const res = await apiClient.post<ApiEnvelope<CustomDesignRequestResponseData>>(
      '/custom-design-requests',
      body,
    );

    const data = unwrap(res.data, res.status);
    return data.customDesignRequest;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Admin list
// ---------------------------------------------------------------------------

/**
 * Fetch the paginated admin custom design requests list.
 *
 * Route: `GET /api/v1/admin/custom-design-requests`
 * Auth:  `auth:console`, scope `design.requests.view`
 *
 * List lives under `data.customDesignRequests`; pagination under
 * `meta.pagination`.
 */
export async function fetchCustomDesignRequests(
  params: FetchCustomDesignRequestsParams = {},
): Promise<FetchCustomDesignRequestsResult> {
  const { page = 1, perPage = 25, status, search, assignedUserId } = params;

  try {
    const res = await apiClient.get<
      ApiEnvelope<AdminCustomDesignRequestListResponseData> & {
        meta: { pagination?: CustomDesignRequestsPaginationMeta };
      }
    >('/admin/custom-design-requests', {
      params: {
        page,
        per_page: perPage,
        ...(status ? { status } : {}),
        ...(search ? { search } : {}),
        ...(assignedUserId !== undefined ? { assigned_user_id: assignedUserId } : {}),
      },
    });

    const data = unwrap(res.data, res.status);
    const pagination = res.data.meta?.pagination;

    return {
      customDesignRequests: data.customDesignRequests,
      pagination: pagination ?? {
        total: data.customDesignRequests.length,
        per_page: perPage,
        page,
        last_page: 1,
        has_more: false,
      },
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Admin detail
// ---------------------------------------------------------------------------

/**
 * Fetch a single custom design request.
 *
 * Route: `GET /api/v1/admin/custom-design-requests/{id}`
 * Auth:  `auth:console`, scope `design.requests.view`
 */
export async function fetchCustomDesignRequest(id: number): Promise<CustomDesignRequestDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<CustomDesignRequestResponseData>>(
      `/admin/custom-design-requests/${id}`,
    );

    const data = unwrap(res.data, res.status);
    return data.customDesignRequest;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Admin status update
// ---------------------------------------------------------------------------

/**
 * Transition a custom design request to a new status.
 *
 * Route: `PATCH /api/v1/admin/custom-design-requests/{id}`
 * Auth:  `auth:console`, scope `design.requests.manage`
 */
export async function updateCustomDesignRequestStatus(
  id: number,
  status: UpdateCustomDesignRequestStatusPayload['status'],
): Promise<CustomDesignRequestDTO> {
  try {
    const res = await apiClient.patch<ApiEnvelope<CustomDesignRequestResponseData>>(
      `/admin/custom-design-requests/${id}`,
      { status },
    );

    const data = unwrap(res.data, res.status);
    return data.customDesignRequest;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Admin assign
// ---------------------------------------------------------------------------

/**
 * Assign (or unassign) a custom design request to a staff account.
 *
 * Route: `PATCH /api/v1/admin/custom-design-requests/{id}/assign`
 * Auth:  `auth:console`, scope `design.requests.manage`
 *
 * `userId: null` unassigns explicitly — the platform Form Request requires
 * `user_id` to be present in the body, so this helper always sends the key.
 * Returns the same detail shape as `fetchCustomDesignRequest` (files with
 * presigned preview/thumbnail URLs included).
 */
export async function assignCustomDesignRequest(
  id: number,
  userId: number | null,
): Promise<CustomDesignRequestDTO> {
  try {
    const res = await apiClient.patch<ApiEnvelope<CustomDesignRequestResponseData>>(
      `/admin/custom-design-requests/${id}/assign`,
      { user_id: userId },
    );

    const data = unwrap(res.data, res.status);
    return data.customDesignRequest;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Admin create quote
// ---------------------------------------------------------------------------

/**
 * Create a quote request from a custom design request.
 *
 * Route: `POST /api/v1/admin/custom-design-requests/{id}/quote`
 * Auth:  `auth:console`, scope `design.requests.manage`
 *
 * No request body. Moves the design request to `quoted` and links the new
 * quote request. Returns `quoteRequestId` (to open the existing quote
 * editor) and `designServiceProductId` (to pre-select the quote-only
 * "Design Service" catalog product as a line item), alongside the updated
 * design request. Rejects with a 409 `ApiError` when the request already
 * has a linked quote or is past the `contacted` stage.
 */
export async function createQuoteFromDesignRequest(
  id: number,
): Promise<CreateQuoteFromDesignRequestResponseData> {
  try {
    const res = await apiClient.post<ApiEnvelope<CreateQuoteFromDesignRequestResponseData>>(
      `/admin/custom-design-requests/${id}/quote`,
    );

    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Admin assignees
// ---------------------------------------------------------------------------

/**
 * Fetch the staff users eligible to be assigned a design request.
 *
 * Route: `GET /api/v1/admin/custom-design-requests/assignees`
 * Auth:  `auth:console`, scope `design.requests.view`
 *
 * Unpaginated — deliberately separate from `@dfq/shared/api/admin/settings`'s
 * `fetchStaffUsers` (which requires `settings.view`, admin/manager only) so
 * every staff role that can see the design queue can also populate the
 * assignee picker.
 */
export async function fetchDesignRequestAssignees(): Promise<DesignRequestAssigneeDTO[]> {
  try {
    const res = await apiClient.get<ApiEnvelope<DesignRequestAssigneesResponseData>>(
      '/admin/custom-design-requests/assignees',
    );

    const data = unwrap(res.data, res.status);
    return data.assignees;
  } catch (err) {
    throwAsApiError(err);
  }
}
